import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, first, map, switchMap } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { LMContextService, LMFormatUtilService, LMLoggerService, LMSpecService, LMUrlUtilService } from "@app/services";
import { Router } from "@angular/router";
import { LMFieldPickerSelectResponse, LMOperatorInfo } from "@modal/field-picker/lm-field-picker";
import { AppConstants, FilterBuilderDialogResult } from "@app/utils";
import {
  ConfirmationDialogResult,
  LMBuilderService,
  LMConfirmationMessageService,
  LMCreateReportDialogService, LMFacadeService,
  LMFieldPickerService,
  LMFilterBuilderService,
  LMSelectDialogService,
  LMSelectResponse
} from "@app/components";
import { ListResource } from "@app/resources";

export type LMBuilderContext = 'DISPLAY_FIELDS'  | 'INSTANCE_SELECTION' | 'TOTALING' | 'COLUMN_TOTAL_FIELDS' | 'SUMMARY_TOTAL_FIELDS' | 'PROPERTIES' | 'TOTAL_BY' | 'UNKNOWN';

export enum LMBuilderEvent{
  DISPLAY_FIELD_ADDED = 'displayFieldAdded',
  DISPLAY_FIELD_REMOVED = 'displayFieldRemoved',
  DISPLAY_FIELD_REORDERED = 'displayFieldReordered',
  DISPLAY_FIELD_SORT_CHANGED = 'displayFieldSortChanged',
  DISPLAY_FIELD_TOTAL_CHANGED = 'displayFieldTotalChanged',
  COLUMN_TOTALS_CHANGED = 'columnTotalsChanged',
  SUMMARY_TOTALS_CHANGED = 'summaryTotalsChanged',
  TITLE_CHANGED ='titleChanged',
  FILTER_CHANGED ='filterChanged'
}

@Injectable()
export class LMBuilderModel implements OnDestroy {
  static SUMMARY_TOTAL_LIST = 'summary-total-list';
  static COLUMN_TOTAL_LIST = 'column-total-list';
  static DISPLAY_FIELDS_LIST = 'display-fields-list';
  static INSTANCE_SELECTION = 'instance-selection';
  static LIST_PREVIEW_EMPTY_STATE = 'list-preview-empty-state';
  static FACADE_FIELD_PICKER_FIELDS_LIST = 'facade-field-picker-fields-list';

  private _builderContext$ = new BehaviorSubject<LMBuilderContext>('UNKNOWN');
  private _builderEvent$ = new Subject<LMBuilderEvent>();
  private facade$!: Subject<LMBusinessClassFacade | undefined>;
  private spec$!: Subject<LMListPSpec | undefined>;

  private _mode!: LMBuilderMode;
  private _listResource!: ListResource;
  private userfolder: string | undefined;
  private _moduleTag: string | undefined;
  private _listImplementation: LMBuilderListImplementation = 'live';
  private facade: LMBusinessClassFacade | undefined;
  private spec: LMListPSpec | undefined;
  private originalSpec: LMListPSpec | undefined;
  private modules: Array<LMBuilderMetaDataOption> | undefined;
  private categoriesByModule: { [module: string]: Array<LMBuilderMetaDataOption> } | undefined;
  private summaryTotalModelMap = new Map<string, LMBuilderTotalFieldModel>();
  private _summaryTotalFields: Array<LMBuilderTotalFieldModel> = [];
  private _summaryInstanceCount = false;

  constructor(
      private httpClient: HttpClient,
      private router: Router,
      private lmContextService: LMContextService,
      private lmUrlUtilService: LMUrlUtilService,
      private lmFacadeService: LMFacadeService,
      private lmBuilderService: LMBuilderService,
      private lmFieldPickerService: LMFieldPickerService,
      private lmFilterBuilderService: LMFilterBuilderService,
      private lmSelectDialogService: LMSelectDialogService,
      private lmSpecService: LMSpecService,
      private lmFormatUtilService: LMFormatUtilService,
      private lmCreateReportDialogService: LMCreateReportDialogService,
      private lmConfirmationMessageService: LMConfirmationMessageService,
      private lmLoggerService: LMLoggerService) {

    const url = this.router.url;
    const resource = this.lmUrlUtilService.getResource(url, false);
    const listParam = this.lmUrlUtilService.getUrlParam(url, 'list');
    this._mode = resource.busClassName.startsWith('CreateReport') ? 'createReport' : 'personalize';
    this._listResource = new ListResource({ resourceId: listParam });

    const userFolderParam = this.lmUrlUtilService.getUrlParam(url, 'userfolder');
    if (userFolderParam) {
      this.userfolder = userFolderParam;
    }

    const moduleTagParam = this.lmUrlUtilService.getUrlParam(url, 'moduletag');
    if (moduleTagParam) {
      this._moduleTag = moduleTagParam;
    }

    const implementationTypeParam = this.lmUrlUtilService.getUrlParam(url, 'reporttype');
    if (implementationTypeParam) {
      this._listImplementation = implementationTypeParam;
    }
  }

  ngOnDestroy(): void {
    this._builderContext$.complete();
    this._builderEvent$.complete();
    this.spec$.complete();
  }

  get mode(): LMBuilderMode {
    return this._mode;
  }

  get listResource(): ListResource {
    return this._listResource;
  }

  get userFolderResource(): string | undefined {
    return this.userfolder;
  }

  get moduleTag(): string | undefined {
    return this._moduleTag;
  }

  get listImplementation(): LMBuilderListImplementation {
    return this._listImplementation;
  }

  public getFacade(): Observable<LMBusinessClassFacade | undefined> {
    if (this.facade != undefined) {                           // already fetched
      return of(this.facade);
    }

    if (this.facade$ != undefined) {                          // fetching
      return this.facade$;
    }

    this.facade$ = new Subject<LMBusinessClassFacade | undefined>();    // first time asked for

    this.lmFacadeService.getBusinessClassFacade(this._listResource.busClassName).subscribe({
      next: (v) => {
        this.handleFacadeResponse(v);
      },
      error: (e) => {
        this.facade = e;
        this.facade$.error(e);
      },
      complete: () => {
      }
    });

    return this.facade$.asObservable();
  }

  private handleFacadeResponse(facade: LMBusinessClassFacade | undefined) {
    this.facade = facade;
    this.facade$.next(this.facade);
    this.facade$.complete();
  }

  public getSpec(): Observable<LMListPSpec | undefined> {
    if (this.spec != undefined) {                           // already fetched
      return of(this.spec);
    }

    if (this.spec$ != undefined) {                          // fetching
      return this.spec$;
    }

    this.spec$ = new Subject<LMListPSpec | undefined>();    // first time asked for

    if (this._mode === 'createReport') {
      this.lmBuilderService.getCreateReportSpec(this._listResource, this._listImplementation, this.userfolder).subscribe({
        next: (v) =>      { this.handleBuilderResponse(v);  },
        error: (e) =>     { this.handleSpecError(e);            },
        complete: () =>   {                                 }
      });
    } else  if (this._mode === 'personalize') {
      this.lmBuilderService.getPersonalizeSpec(this._listResource, this.userfolder).subscribe({
        next: (v) =>      { this.handleBuilderResponse(v);  },
        error: (e) =>     { this.handleSpecError(e);            },
        complete: () =>   {                                 }
      });
    }

    return this.spec$.asObservable();
  }

  private handleBuilderResponse(spec: LMListPSpec | undefined) {
    if (this.lmBuilderService.responseHasError(spec)) {
      this.handleSpecError(spec);
      return;
    }
    
      this.spec = spec;
      this.originalSpec = JSON.parse(JSON.stringify(spec));
      this.spec$.next(this.spec);
      this.spec$.complete();
  }

  private handleSpecError(error: any) {
    this.spec = error;
    this.spec$.error(error);
  }

  get builderContext$(): BehaviorSubject<LMBuilderContext> {
    return this._builderContext$;
  }

  get builderEvent$(): Subject<LMBuilderEvent> {
    return this._builderEvent$;
  }

  public buildDefaultReportName(): string {
    let defaultName = 'Report';

    if (this.spec && this.facade) {
      const userFolderName = this.spec.userFolder?.name ? this.spec.userFolder.name : '';

      if (userFolderName) {
        defaultName = `Copy of ${ userFolderName }`;
      } else {
        const listResource = this.listResource;
        if (listResource?.listName) {
          const listTitle = this.spec.titleIs.display;
          defaultName = `Copy of ${ listTitle }`;
        } else {
          const busClassName = this.facade.name ? this.facade.name : this.spec.businessClass;
          defaultName = `New ${ busClassName } Report`;
        }
      }
    }

    return defaultName;
  }

  public getReportCategories(category?: string): Observable<Array<LMBuilderMetaDataOption>> {
    if (this.modules) {
      return of(this.modules);
    }
    if(this.spec) {
      return this.lmCreateReportDialogService.getModules(this.spec.businessClass, this.spec.listName).pipe(
          map((response: { modules: LMAppReport['modules'] }) => {
            this.buildReportCategories(response, category);
            return this.modules ? this.modules : [];
          })
      );
    } else {
      return of([]);
    }
  }

  private buildReportCategories( response:{ modules: LMAppReport['modules'] }, category?: string ){
    this.modules = [];

    if (response.modules) {
      const modules = response.modules;
      let addMissing = category ? true : false;
      modules.forEach(module => {
        if (module.value === category) {
          addMissing = false;
        }

        // @ts-ignore
        this.modules.push({ value:module.value, label: module.label });
      });

      if (addMissing) {
        // @ts-ignore
        this.modules.push({ value:category, label: category });
      }
    }
  }

  public getReportSubcategories(category: string, subCategory?:string): Observable<Array<LMBuilderMetaDataOption>> {
    if (this.categoriesByModule) {
      const subcategories = this.categoriesByModule.hasOwnProperty(category) ? this.categoriesByModule[category] : [];
      return of(subcategories);
    }

    if(this.spec) {
       return this.lmCreateReportDialogService.getCategoriesByModule(this.spec.businessClass, this.spec.listName).pipe(
          map((response:  { categoriesByModule: LMAppReport['categoriesByModule'] }) => {
            this.buildSubcategoriesForModules(category, response, subCategory);
            return this.categoriesByModule && this.categoriesByModule.hasOwnProperty(category) ? this.categoriesByModule[category] : [ { value: '', label: 'General' } ];
          })
      );
    } else {
      return of([]);
    }
  }

  private buildSubcategoriesForModules(category: string, response: { categoriesByModule: LMAppReport['categoriesByModule'] }, subcategory?: string){
    this.categoriesByModule  = {};

    if (response.categoriesByModule) {

      const categoriesByModule = response.categoriesByModule;

      Object.keys(categoriesByModule).forEach(module => {
        let addMissing = module === category && subcategory ? true : false;
        const subcategories:Array<LMBuilderMetaDataOption> = [];
        const options: LMAppReportOption[] = categoriesByModule[module];
        options.forEach(option => {
          if (option.value === subcategory) {
            addMissing = false;
          }
          // @ts-ignore
          subcategories.push({ value:option.value, label: option.label });
        });

        if (addMissing) {
          // @ts-ignore
          subcategories.push({ value:subcategory, label: subcategory });
        }
        subcategories.push({ value:'', label: 'General' });
        // @ts-ignore
        this.categoriesByModule[module] = subcategories;
      });
    }
  }

  public getBuilderUrl(): string{
    if (this._mode === 'createReport') {
      return this.lmBuilderService.getCreateReportSpecUrl(this._listResource, this._listImplementation, this.userfolder);
    } else  if (this._mode === 'personalize') {
      return this.lmBuilderService.getPersonalizeSpecUrl(this._listResource, this.userfolder);
    }

    return '';
  }

  public allowShowLines(): boolean {
    if (this.spec) {
      return !(this.spec.implementsTreeView || this.spec.implementsCubeView);
    }

    return false;
  }
  public buildPageSizes(defaultPageSize: number | undefined): Array<LMBuilderMetaDataOption> {
    if (this.spec) {
      let pageSizes: Array<LMBuilderMetaDataOption> = [];
      pageSizes.push({ value: 'default', label: 'Default' });  // needs translation

      AppConstants.PAGE_SIZES.forEach(value => {
        pageSizes.push({ value: `${value}`, label: `${value}` });
      });

      if (defaultPageSize) {
        if (!AppConstants.PAGE_SIZES.includes(defaultPageSize)) {
          pageSizes = pageSizes.concat({ value: `${defaultPageSize}`, label: `${defaultPageSize}` }).sort((a, b) => a.label.localeCompare(b.label));
        }
      }


      return pageSizes;
    }

    return [];
  }

  public updatePdfOrientation(orientation:LMBuilderPDFOrientation) {
    if (this.spec) {
      if (this.spec.userFolder) {
        this.spec.userFolder.exportOverride.pdfOrientation = orientation;
      } else {
        this.spec.pdfOrientation = orientation;
      }
    }
  }

  public isAuditDataSource(): boolean {
    if (this.spec) {
      if (this.spec.isAuditList) {
        return true;
      }

      if (this.spec.userFolder) {
        return this.spec.userFolder.isAuditData;
      }
    }
    return false;
  }

  public updateAuditData(auditData: boolean) {
    if (this.spec && this.spec.userFolder) {
      this.spec.userFolder.isAuditData = auditData;
    }
  }

  public allowDataAuditReport(): boolean {
    if (this.spec && this.spec.userFolder &&                                                                    // report
      !(this.spec.implementsCubeView || this.spec.implementsColumnarView || this.spec.isAuditList)) {           // transactional data
      return true;
    }

    return false;
  }

  public updateAsOfDate(asOfDate: number) {
    if (this.spec && this.spec.userFolder) {
      this.spec.userFolder.asOfDate = asOfDate;
    }
  }
  public allowAsOfDateReport(): boolean {
    if (this.spec && this.spec.userFolder &&                                              // report
      !(this.spec.implementsCubeView || this.spec.implementsColumnarView) &&              // transactional data
       (this.spec.implementsAuditing || this.spec.implementsEffectiveDated) &&            // business class can time travel
       (this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_AsOfDate))) {   // toggle on
      return true;
    }
    return false;
  }

  public updateArchiveData(archiveData: boolean) {
    if (this.spec && this.spec.userFolder) {
      this.spec.userFolder.isArchiveData = archiveData;
    }
  }

  public allowArchiveReport(): boolean {
    if (this.spec && this.spec.userFolder &&                                                // report
        this.spec.implementsArchivable &&                                                   // business class can archive
        (this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_ShowArchive))) { // toggle on
     return true;
    }

    return false;
  }

  public allowRestrictedActions(): boolean {
    return this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_Configuration);
  }

  public validateTitle():any {
    return {
      check: (value: string, field: any, grid: any) => {
        let valid = true;
        if (this.spec?.userFolder) {
          valid = value.trim().length > 0;
        }
        return valid;
      },
        id: 'custom',
        type: 'error',
        message: Soho.Locale.translate('Required')
    };
  }

  public validateRequired():any {
    return {
      check: (value: string, field: any, grid: any) => value.trim().length > 0,
      id: 'custom',
      type: 'error',
      message: Soho.Locale.translate('Required')
    };
  }

  public validatePDFCustomPaperSize():any {
    return {
      check: (value: string, field: any, grid: any) => {
        let valid = true;
        if (this.spec?.userFolder?.exportOverride.pdfPaperSize.paperSize === 'Custom') {
          valid = value.trim().length > 0;
        }
        return valid;
      },
      id: 'custom',
      type: 'error',
      message: Soho.Locale.translate('Required')
    };
  }

  public updateTitle(title: string) {
    if (this.spec) {
      if (this.spec.userFolder) {
        this.spec.userFolder.name = title;
      } else {
        const defaultName = this.toSentenceCase(this.spec.listName);
        title = title.trim().length == 0 ? '_' : title;
        this.updateLabel(this.spec.titleIs, title, defaultName);
      }

      this.builderEvent$.next(LMBuilderEvent.TITLE_CHANGED);
    }
  }

  public updateSharing(sharing: LMBuilderSharingType, sharingTarget: string) {
    if (this.spec?.userFolder) {
      this.spec.userFolder.sharing = sharing;

      if (sharing === 'role' || sharing === 'actorGroup' || sharing === 'roleGroup') {
        this.spec.userFolder.sharingTarget = sharingTarget;
      } else {
        this.spec.userFolder.sharingTarget = '';
      }
    }
  }

  public allowSharing(): boolean {

    if (this.spec && this.spec.userFolder &&                                                          // report
        this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_UseAllActorGroups)) {       // toggle on
      return true;
    }

    return false;
  }

  public sharingOptions(): Array<LMBuilderSharingOption> {
    const options:Array<LMBuilderSharingOption> = [];
    options.push({ value: 'private', label: Soho.Locale.translate('CreateReportPrivate') });
    options.push({ value: 'actorGroup', label: Soho.Locale.translate('CreateReportGroup') });
    options.push({ value: 'role', label: Soho.Locale.translate('CreateReportRoleLabel') });
    options.push({ value: 'roleGroup', label: Soho.Locale.translate('CreateReportRoleGroupLabel') });
    options.push({ value: 'public', label: Soho.Locale.translate('CreateReportPublic') });
    
    return options;
  }

  public showActorGroupSelect(): Observable<LMDataView | undefined>  {
    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    const urlInfo: LMListUrlInfo = { basePath: baseUrl };
    const selectListUrl = this.lmUrlUtilService.buildActorGroupSelectUrl(urlInfo);
    const businessClass = 'ActorGroup';
    const viewName = 'ActorGroupHierarchyList';
    const title = Soho.Locale.translate('CreateReportActorGroupLabel');

    return this.showSelectList(selectListUrl, businessClass, viewName, title);
  }

  public showRoleGroupSelect(): Observable<LMDataView | undefined>  {
    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    const urlInfo: LMListUrlInfo = { basePath: baseUrl };
    const selectListUrl = this.lmUrlUtilService.buildRoleGroupSelectUrl(urlInfo);
    const businessClass = 'RoleGroup';
    const viewName = 'RoleGroupList';
    const title = Soho.Locale.translate('CreateReportRoleGroupLabel');

    return this.showSelectList(selectListUrl, businessClass, viewName, title);
  }

  public showRoleSelect(): Observable<LMDataView | undefined> {
    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    const urlInfo: LMListUrlInfo = { basePath: baseUrl };
    const selectListUrl = this.lmUrlUtilService.buildRoleSelectUrl(urlInfo);
    const businessClass = 'Role';
    const viewName = 'RoleList';
    const title = Soho.Locale.translate('CreateReportRoleLabel');
    return this.showSelectList(selectListUrl, businessClass, viewName, title);
  }

  private showSelectList(selectListUrl: string, businessClass: string, viewName: string, title: string): Observable<LMDataView | undefined> {
    const response = new Subject<LMDataView | undefined>();

    this.lmSpecService.getListSpec(selectListUrl).subscribe({
      next: (v)         => { this.handleShowSelectResponse(response, businessClass, viewName, title, v);           },
      error: (e)        => { this.handleShowSelectError(response, e);                           },
      complete: ()      => {                                                }
    });

    return response.asObservable();
  }

  private handleShowSelectResponse(subject: Subject<LMDataView | undefined>, businessClass: string, viewName: string, title: string, listSpecResponse: LMListSpecResponse | LMSystemException) {
    if ('listUrlInfo' in listSpecResponse && 'listSpec' in listSpecResponse) {
      const listUrlInfo = listSpecResponse.listUrlInfo;
      const listSpec = listSpecResponse.listSpec;
      const dataView: LMDataView = {
        resourceId: businessClass,
        representativeText: '',
        status: '',
        name: '',
        action: '',
        busClassName: businessClass,
        navigations: [],
        contextProperties: null,
        actionMeta: undefined,
        fields: {}
      };

      const selectRequestData: SelectRequestData = { viewType: 'form', viewName, dataView };

      this.lmSelectDialogService.showSelectListDialog({
        listUrlInfo,
        listSpec,
        title,
        sourceField: businessClass,
        selectRequestData
      }).pipe(
          first()) // causes the subscription to complete after its messaged the first time
          .subscribe((selectResponse: LMSelectResponse) => {
                this.lmLoggerService.log([ 'LMReportPersonalizationDialogComponent', 'showSelectListDialog()', selectResponse ]);
                if (selectResponse?.status === AppConstants.STATUS_COMPLETED && selectResponse.dataViews) {
                  const selectedDataView = selectResponse.dataViews[0];
                  subject.next(selectedDataView);
                  subject.complete();
                } else if (selectResponse?.status === AppConstants.STATUS_CANCEL) {
                  subject.next(undefined);
                  subject.complete();
                }
              }
          );
      } else {
      subject.next(undefined);
      subject.complete();
    }
  }

  private handleShowSelectError(subject: Subject<LMDataView | undefined>, error: any) {
    subject.next(undefined);
    subject.complete();
  }

  public reportHasBaseFilter() : boolean {
    if (this.spec && this.spec.userFolder && this.originalSpec) {
      return this.originalSpec.instanceSelection.where.trim().length > 0;
    }

    return false;
  }

  public reportBaseFilter() : string {
    if (this.spec && this.spec.userFolder && this.originalSpec) {
      return this.originalSpec.instanceSelection.where;
    }

    return '';
  }

  public getFilterCriteria(): string {
    if (this.spec) {
      return this.spec.userFolder ? this.spec.userFolder.search : this.spec.instanceSelection.where;
    }
    return '';
  }

  public getConditionTerms(): Array<LMBuilderConditionTerm> | undefined {
    if (this.spec) {
      return this.spec.userFolder ? this.spec.userFolder.conditionTerms : this.spec.instanceSelection.conditionTerms;
    }
    return undefined;
  }

  public removeReportBaseFilter() {
    if (this.spec) {
      this.spec.instanceSelection.where = '';
    }
  }
  public revertReportBaseFilter() {
    if (this.spec && this.originalSpec) {
      this.spec.instanceSelection.where = this.originalSpec.instanceSelection.where;
    }
  }

  public addCustomFilter(fieldId?: string) {
    const filterCriteria = this.getFilterCriteria().trim();
    let fieldPickerUrl = this.getBuilderUrl();
    fieldPickerUrl = this.lmUrlUtilService.addParamToUrl(fieldPickerUrl, 'requesttype', 'fieldSpecForBusClass');
    const validationUrl = this.lmUrlUtilService.getConfigUrl(this.listResource.resourceId);
    let subjectInitialValue : LMBuilderConditionTermDetail | undefined;

    if (fieldId) {
      subjectInitialValue =  {
        subject: fieldId,
      };
    }

    const options: LMFilterBuilderOptions = {
      businessClass: this.listResource.busClassName,
      fieldLookUpUrl: fieldPickerUrl,
      fieldSearchUrl:  fieldPickerUrl,
      lplValidationUrl: validationUrl,
      hasExistingCondition: filterCriteria.length > 0,
      fieldPickerView: 'InstanceSelection',
      subjectInitialValue: subjectInitialValue,
      includeAuditFields: this.isAuditDataSource()
    };

    this.lmFilterBuilderService.openBuilderDialog(options).subscribe(response => {
      if (response.dialogResult && response.lplQuery) {
        let filterText = response.lplQuery;

        if (fieldId) {
          // building just a single condition so lets remove the parens
          filterText = response.lplQuery.substring(1, response.lplQuery.length -1);
        }

        switch (response.dialogResult) {
          case FilterBuilderDialogResult.CONFIRMATION_OK:
          case FilterBuilderDialogResult.CONFIRMATION_OVERWRITE:
            this.updateFilterCriteria(filterText);
            this.updateConditionTerms(response.conditionTerms);
            this.builderEvent$.next(LMBuilderEvent.FILTER_CHANGED);
            break;
          case FilterBuilderDialogResult.CONFIRMATION_APPEND:
            if (this.getFilterCriteria().trim().length > 0) {
              this.updateFilterCriteria(`${filterCriteria} ${response.matchOption} ${filterText}`);
              const conditionTerms = this.getConditionTerms();

              if (conditionTerms && conditionTerms.length > 0 && response.conditionTerms) {
                response.conditionTerms.forEach(term => {
                  conditionTerms.push(term);
                });
              }
            } else {
              this.updateFilterCriteria(filterText);
              this.updateConditionTerms(response.conditionTerms);
            }
            this.builderEvent$.next(LMBuilderEvent.FILTER_CHANGED);
        }
      }
    });
  }

  public validateFilterCriteria(filterCriteria: string):Observable<LMLplValidationResponse>  {
    if (filterCriteria.trim().length === 0) {
      this.updateConditionTerms([]);
      return of({ lplStatus: 'LPL_VALID' });
    }

    if (filterCriteria.trim() === this.getFilterCriteria().trim()) {
      return of({ lplStatus: 'LPL_VALID' });
    }

    let url = this.getBuilderUrl();
    url = this.lmUrlUtilService.addParamToUrl(url, 'requesttype', 'validate-condition');

    const payload = {
      businessClass: this.listResource.busClassName,
      condition: filterCriteria
    };

    return this.execute(url, payload);
  }

  public updateFilterCriteria(filterCriteria: string) {
    if (this.spec) {
      if (this.spec.userFolder) {
        this.spec.userFolder.search = filterCriteria;
      } else {
        this.spec.instanceSelection.where = filterCriteria;
      }
    }
  }

  public updateConditionTerms(conditionTerms: Array<LMBuilderConditionTerm> | undefined) {
    if (this.spec) {
      if (this.spec.userFolder) {
        this.spec.userFolder.conditionTerms = conditionTerms;
      } else {
        this.spec.instanceSelection.conditionTerms = conditionTerms;
      }
    }
  }

  public validateSimpleCondition(simpleCondition: string):Observable<LMLplValidationResponse>  {
    if (simpleCondition.trim().length === 0) {
      return of({ lplStatus: 'LPL_VALID' });
    }

    let url = this.getBuilderUrl();
    url = this.lmUrlUtilService.addParamToUrl(url, 'requesttype', 'validate-simple-condition');

    const payload = {
      businessClass: this.listResource.busClassName,
      simpleCondition: simpleCondition
    };

    return this.execute(url, payload);
  }

  public validateCompute(computeText: string):Observable<LMLplValidationResponse>  {
    if (computeText.trim().length === 0) {
      return of({ lplStatus: 'LPL_VALID' });
    }

    let url = this.getBuilderUrl();
    url = this.lmUrlUtilService.addParamToUrl(url, 'requesttype', 'validate-inline-compute');

    const payload = {
      businessClass: this.listResource.busClassName,
      computeText: computeText
    };

    return this.execute(url, payload);
  }

  public initializeSummaryTotalFields() {
    this.summaryTotalModelMap.clear();

    if (this.spec?.summaryTotalFields) {
      this.spec.summaryTotalFields.forEach(summaryTotal => {
        if (summaryTotal.fieldId === 'instance count') {
          this._summaryInstanceCount = true;
        } else {
          const firstSpace = summaryTotal.fieldId.indexOf(" ");
          const operator = summaryTotal.fieldId.substring(0, firstSpace);
          const fieldId = summaryTotal.fieldId.substring(firstSpace + 1);

          let model: LMBuilderTotalFieldModel;
          if (this.summaryTotalModelMap.has(fieldId)) {
            // @ts-ignore
            model = this.summaryTotalModelMap.get(fieldId);
          } else {
            model = this.buildSummaryTotalFieldModel(fieldId, summaryTotal.type);
            this.summaryTotalModelMap.set(fieldId, model);
          }

          // @ts-ignore
          const operatorModel = model.operatorModels.find(operatorModel => operatorModel.operator === operator);

          if (operatorModel) {
            operatorModel.checked = true;
            operatorModel.summaryTotalSpec = summaryTotal;
          }
        }
      });

      this.buildSummaryTotalFields();
    }
  }

  get summaryInstanceCount(): boolean {
    return this._summaryInstanceCount;
  }

  get summaryTotalFields(): Array<LMBuilderTotalFieldModel> {
    return this._summaryTotalFields;
  }

  public addSummaryTotalField(builderField: LMBuilderField) {
    if (this.spec) {
      // spec isn't changed, need to check a summary total operator first!
      if (this.allowSummaryTotal(builderField)) {
        const fieldId = this.buildFieldId(builderField);
        this._addSummaryTotalField(fieldId, builderField.icon === 'number' ? 'number' : 'date');
      }
    }
  }

  public addSummaryTotalFieldForUiFieldSpec(result: LMFieldPickerSelectResponse) {
    if (this.spec) {
      const fieldId = this.lmFieldPickerService.buildFieldNameWithOperatorInfo(result.uiFieldSpec, result.operatorInfo ? result.operatorInfo : {}, 'Fields');
      if (this.allowSummaryTotalForUiFieldSpec(result.uiFieldSpec)) {
        this._addSummaryTotalField(fieldId, result.uiFieldSpec.fieldType === 'NUMBER' ? 'number' : 'date');
      }
    }
  }

  private _addSummaryTotalField(fieldId: string, type: 'number' | 'date') {
    if (!this.summaryTotalModelMap.has(fieldId)) {
      const totalModel = this.buildSummaryTotalFieldModel(fieldId, type);
      this.summaryTotalModelMap.set(fieldId, totalModel);
      this._summaryTotalFields.push(totalModel);

      const operatorModel = totalModel.operatorModels.find(model => model.operator === 'sum');

      if (operatorModel) {
        operatorModel.checked = true;
        const summaryFieldId = `${operatorModel.operator} ${fieldId}`;
        const spec = this.createSummaryTotalSpec(summaryFieldId, type);
        operatorModel.summaryTotalSpec = spec;

        if (this.spec) {
          this.spec.summaryTotalFields.push(spec);
        }
      }

      this.builderEvent$.next(LMBuilderEvent.SUMMARY_TOTALS_CHANGED);
    }
  }

  public removeSummaryTotalField(totalFieldModel: LMBuilderTotalFieldModel) {
    if (this.spec) {
      const fieldId = totalFieldModel.fieldId;
      this.summaryTotalModelMap.delete(fieldId);
      if (this.spec?.summaryTotalFields) {
        this.spec.summaryTotalFields = this.spec.summaryTotalFields.filter(summaryTotalSpec => {
          const firstSpace = summaryTotalSpec.fieldId.indexOf(" ");
          const summaryTotalFieldId = summaryTotalSpec.fieldId.substring(firstSpace + 1);
          return summaryTotalFieldId !== fieldId;
        });
      }
      this.buildSummaryTotalFields();
    }
  }

  public updateSummaryTotalField(totalFieldModel: LMBuilderTotalFieldModel, operatorModel: LMBuilderTotalOperatorModel) {
    if (this.spec) {
      const fieldId = `${operatorModel.operator} ${totalFieldModel.fieldId}`;
      if (operatorModel.checked) {
        const spec = this.createSummaryTotalSpec(fieldId, totalFieldModel.type);
        operatorModel.summaryTotalSpec = spec;
        this.spec.summaryTotalFields.push(spec);
        this.orderSummaryTotalFields();
      } else {
        this.removeSummaryTotalSpec(fieldId);
      }

      this.builderEvent$.next(LMBuilderEvent.SUMMARY_TOTALS_CHANGED);
    }
  }

  public updateSummaryInstanceCount(countRecords: boolean) {
    if (this.spec) {
      this._summaryInstanceCount = countRecords;
      if (this.spec) {
        const fieldId = 'instance count';
        if (countRecords) {
          this.spec.summaryTotalFields.push(this.createSummaryTotalSpec(fieldId, undefined));
          this.orderSummaryTotalFields();
        } else {
          this.removeSummaryTotalSpec(fieldId);
        }
        this.orderSummaryTotalFields();
      }
      this.builderEvent$.next(LMBuilderEvent.SUMMARY_TOTALS_CHANGED);
    }
  }

  public reorderSummaryTotalField(currentIndex: number, previousIndex: number, totalFieldModel: LMBuilderTotalFieldModel) {
    if (this.spec) {
      if (previousIndex !== currentIndex) {
        // remove previous location
        this._summaryTotalFields.splice(previousIndex, 1);
        // add at new location
        this._summaryTotalFields.splice(currentIndex, 0, totalFieldModel);
      }

      this.orderSummaryTotalFields();
    }
  }

  private orderSummaryTotalFields() {
    if (this.spec) {
      const staged: Array<LMSummaryTotalPSec> = [];
      const instanceCountSpec = this.findSummaryTotalSpec("instance count");
      if (instanceCountSpec) {
        staged.push(instanceCountSpec);
      }

      this._summaryTotalFields.forEach(totalFieldModel => {
        totalFieldModel.operatorModels.forEach(operatorModel => {
          if (operatorModel.checked) {
            // @ts-ignore
            staged.push(operatorModel.summaryTotalSpec);
          }
        });
      });

      this.spec.summaryTotalFields = staged;
    }
  }

  private findSummaryTotalSpec(fieldId: string): LMSummaryTotalPSec | undefined {
    if (this.spec) {
      return this.spec.summaryTotalFields.find(spec => spec.fieldId === fieldId);
    }
    return undefined;
  }

  private buildSummaryTotalFieldModel(fieldId:string, type: 'number' | 'date' | undefined): LMBuilderTotalFieldModel {
    const operatorModels:Array<LMBuilderTotalOperatorModel> = [];
    operatorModels.push(this.buildSummaryTotalOperatorModel(fieldId, type, 'sum'));
    operatorModels.push(this.buildSummaryTotalOperatorModel(fieldId, type, 'avg'));
    operatorModels.push(this.buildSummaryTotalOperatorModel(fieldId, type, 'min'));
    operatorModels.push(this.buildSummaryTotalOperatorModel(fieldId, type, 'max'));

    return  {
      fieldId: fieldId,
      type: type,
      operatorModels: operatorModels
    };
  }

  private buildSummaryTotalOperatorModel(fieldId:string, type: 'number' | 'date' | undefined, operator: string, summaryTotalSpec?: LMSummaryTotalPSec): LMBuilderTotalOperatorModel {
    const checked = summaryTotalSpec != undefined;
    let label = '';
    if (operator === 'sum') {
      label = Soho.Locale.translate('SummarySumOperator');
    } else if (operator === 'avg') {
      label = Soho.Locale.translate('SummaryAvgOperator');
    } else if (operator === 'min') {
      label = type === 'date' ? Soho.Locale.translate('EarliestOperator') : Soho.Locale.translate('SummaryMinOperator');
    } else if (operator === 'max') {
      label = type === 'date' ? Soho.Locale.translate('LatestOperator') : Soho.Locale.translate('SummaryMaxOperator');
    } else {
      label = operator;
    }

    return  {
      operator:  operator,
      label: label,
      checked: checked,
      summaryTotalSpec: summaryTotalSpec,
      dai: `lm-builder-summary-total-${fieldId}-${operator}`
    };
  }

  private buildSummaryTotalFields() {
    const stagedTotalFields: Array<LMBuilderTotalFieldModel> = [];
    this.summaryTotalModelMap.forEach((model, fieldId) => {
      stagedTotalFields.push(model);
    });
    this._summaryTotalFields = stagedTotalFields;
  }

  private createSummaryTotalSpec(fieldId: string, type: 'number' | 'date' | undefined): LMSummaryTotalPSec {
    return {
      key: -1,
      fieldId: fieldId,
      type: type,
      fieldLabel: '',
      label: { type: 'default', messageRule: '', display: '' }
    };
  }

  private removeSummaryTotalSpec(fieldId: string) {
    if (this.spec) {
      this.spec.summaryTotalFields = this.spec.summaryTotalFields.filter(summaryTotalSpec => summaryTotalSpec.fieldId !== fieldId);
    }
  }

  public allowSummaryTotal(builderField: LMBuilderField) {
    if (builderField.facadeType === 'field') {
      const facadeField: LMFacadeField = builderField?.facade as LMFacadeField;
      const uiFieldSpec: UIFieldSpec = facadeField.uiFieldSpec;

      if (uiFieldSpec) {
        return this.allowSummaryTotalForUiFieldSpec(uiFieldSpec);
      }
    }

    return false;
  }

  public allowSummaryTotalForUiFieldSpec(uiFieldSpec: UIFieldSpec): boolean {
    if (uiFieldSpec.isStateField || uiFieldSpec.isKeyField) {
        return false;
    }

    if (uiFieldSpec.canTotal) {
      return true;
    }

    if (uiFieldSpec.fieldType === 'DATE' || uiFieldSpec.fieldType === 'TIMESTAMP') {
      return true;
    }

    return false;
  }

  public allowColumnTotal(builderField: LMBuilderField): boolean {
    if (builderField.facadeType === 'field') {
      const facadeField: LMFacadeField = builderField?.facade as LMFacadeField;
      return this.allowColumnTotalForUiFieldSpec(facadeField.uiFieldSpec);
    }

    return false;
  }

  public allowColumnTotalForUiFieldSpec(uiFieldSpec: UIFieldSpec): boolean {
    return uiFieldSpec?.canTotal ? true : false;
  }

  public allowColumnField(builderField: LMBuilderField): boolean {
    return (builderField.facadeType === 'field' || builderField.facadeType === 'condition');
  }

  public allowColumnFieldForUiFieldSpec(uiFieldSpec: UIFieldSpec): boolean {
    return 'OTO_RELATION' !== uiFieldSpec.fieldType && 'OTM_RELATION' !== uiFieldSpec.fieldType;
  }

  public addComputeDisplayField(): LMDisplayFieldPSpec | undefined {
    if (this.spec) {
      if (!this.spec.displayFields) {
        this.spec.displayFields = [];
      }
      const displayFields = this.spec.displayFields;
      const displayField: LMDisplayFieldPSpec = this.buildComputeDisplayField();
      this.insertField(-1, displayFields, displayField);
      return displayField;
    }
    return undefined;
  }
  public addColumnTotalDisplayField(currentIndex: number, builderField: LMBuilderField) {
    this._addDisplayField(currentIndex, builderField, true);
  }

  public addDisplayField(currentIndex: number, builderField: LMBuilderField) {
    this._addDisplayField(currentIndex, builderField, false);
  }

  private _addDisplayField(currentIndex: number, builderField: LMBuilderField, total: boolean) {
    if (this.spec) {
      if (!this.spec.displayFields) {
        this.spec.displayFields = [];
      }

      const displayFields = this.spec.displayFields;

      if (builderField.facade) {
        const facadeField = builderField.facade as LMFacadeField;
        let fieldId = this.buildFieldId(builderField);
        const label = facadeField.labelMessageRule ? facadeField.labelMessageRule : facadeField.name;
        const parentChain = builderField.parent ? this.buildFieldId(builderField.parent) : '';
        const parentTypeChain = builderField.parent ? this.buildParentTypeChain(builderField.parent) : '';
        const isOTM = parentTypeChain.startsWith('OTM_RELATION');

        facadeField.uiFieldSpec.parent = parentChain;
        facadeField.uiFieldSpec.parentFieldTypes = parentTypeChain;

        if (isOTM) {
          fieldId = `first ${fieldId}`;
        }

        this.runAddColumnChecks(fieldId, isOTM, label, facadeField.uiFieldSpec.businessClass).
          subscribe(
            (result: any) => {
              if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
                const displayField: LMDisplayFieldPSpec = this.buildDisplayField(fieldId, label, facadeField.uiFieldSpec, total);
                this.insertField(currentIndex, displayFields, displayField);

                if (total) {
                  this.builderEvent$.next(LMBuilderEvent.COLUMN_TOTALS_CHANGED);
                }
              }
            }
        );
      }
    }
  }

  public addColumnTotalDisplayFieldForUiFieldSpec(currentIndex: number, result: LMFieldPickerSelectResponse) {
    this._addDisplayFieldForUiFieldSpec(currentIndex, result, true);
  }

  public addDisplayFieldForUiFieldSpec(currentIndex: number, result: LMFieldPickerSelectResponse) {
    this._addDisplayFieldForUiFieldSpec(currentIndex, result, false);
  }

  private _addDisplayFieldForUiFieldSpec(currentIndex: number, result: LMFieldPickerSelectResponse, total: boolean) {
    if (this.spec) {
      if (!this.spec.displayFields) {
        this.spec.displayFields = [];
      }

      const displayFields = this.spec.displayFields;
      const uiFieldSpec = result.uiFieldSpec;
      const fieldId = result.fieldName;
      const label = uiFieldSpec.label? uiFieldSpec.label : this.toSentenceCase(uiFieldSpec.fieldName);
      const isOTM = this.lmFieldPickerService.isOTMField(uiFieldSpec);

      this.runAddColumnChecks(fieldId, isOTM, label, uiFieldSpec.businessClass).
      subscribe(
          (result: any) => {
            if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
              const displayField: LMDisplayFieldPSpec = this.buildDisplayField(fieldId, label, uiFieldSpec, total);
              this.insertField(currentIndex, displayFields, displayField);
            }
          }
      );
    }
  }

  private isExistingColumnField(fieldId: string): boolean {
    let displayField = undefined;

    if (this.spec && this.spec.displayFields && this.spec.displayFields.length > 0) {
      displayField = this.spec.displayFields.find(df => {
        if (df.type === 'field') {
          const lmFieldPSpec: LMFieldPSpec = df.spec as LMFieldPSpec;
          return lmFieldPSpec.fieldId === fieldId;
        }
        return false;
      });
    }

    return displayField !== undefined;
  }

  private runAddColumnChecks(fieldId: string, otm: boolean, label: string, relatedBusinessClass: string): Observable<ConfirmationDialogResult> {
    return this.alreadyExistsCheck(fieldId).pipe(
        switchMap((confirmation: ConfirmationDialogResult) => {
          if (confirmation === ConfirmationDialogResult.CONFIRMATION_CANCEL) {
            return of(ConfirmationDialogResult.CONFIRMATION_CANCEL);
          }
          if (otm) {
            return this.otmConfirmation(label, relatedBusinessClass);
          }
          return of(ConfirmationDialogResult.CONFIRMATION_OK);
        })
    );
  }

  private alreadyExistsCheck(fieldId: string): Observable<ConfirmationDialogResult>{
    if (this.isExistingColumnField(fieldId)) {
      const confirmationTitle = Soho.Locale.translate('DuplicateColumnWarningTitle');
      const confirmationMessage = Soho.Locale.translate('DuplicateColumnWarningMessage');
      return this.lmConfirmationMessageService.promptConfirmationMessage({ title: confirmationTitle, message: confirmationMessage });
    } else {
      return of(ConfirmationDialogResult.CONFIRMATION_OK);
    }
  }

  private otmConfirmation(label: string, relatedBusinessClass: string): Observable<ConfirmationDialogResult>{
    const confirmationTitle =  Soho.Locale.translate('OTMWarningTitle');
    const confirmationMessage1 = Soho.Locale.translate('OTMWarningMessage1');
    const confirmationMessage2 = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('OTMWarningMessage2'), [label, relatedBusinessClass]);
    const confirmationMessage = `<p>${confirmationMessage1}</p><p>${confirmationMessage2}</p>`;
    return this.lmConfirmationMessageService.promptConfirmationMessage({ title: confirmationTitle, message: confirmationMessage });
  }

  public insertField(currentIndex: number, displayFields: Array<LMDisplayFieldPSpec>, displayField: LMDisplayFieldPSpec) {
    if (displayFields.length === 0 || currentIndex === -1) {
      displayFields.push(displayField);
    } else {
      displayFields.splice(currentIndex, 0, displayField);
    }
    if (this.spec && this.spec.displayFields) {
      if (this.spec.frozenColumn > this.spec.displayFields.length) {
        this.spec.frozenColumn = this.spec.displayFields.length;
      }
    }
    this.builderEvent$.next(LMBuilderEvent.DISPLAY_FIELD_ADDED);
  }

  public buildAlert() : LMAlertPSpec {
    const mouseOverTextIs:LMLabelPSpec = {
      type: 'explicit',
      messageRule: '',
      display: ''
    };

    const when: LMSimpleConditionPSpec = {
      negated: false,
      condition: '',
    };

    return {
      when: when,
      mouseOverTextIs: mouseOverTextIs
    };
  }
  private buildComputeDisplayField(): LMDisplayFieldPSpec {
    const labelSpec:LMLabelPSpec = {
      type: 'explicit',
      messageRule: 'Inline Compute',
      display: ''
    };

    const fieldSpec: LMFieldPSpec = {
      invalidField: false,
      fieldId: '',
      inlineCompute: true,
      fieldLabel: '',
      allowUpdate: { allowUpdate: false, when: '' },
      total: 'NONE',
      canTotal: true,
      canUpdate: false,
      uiFieldSpec: undefined
    };

    return  {
      key: -1,
      type: 'field',
      label: labelSpec,
      columnWidth: 'auto',
      alerts: {},
      spec: fieldSpec
    };
  }

  private buildDisplayField(fieldId: string, label: string, uiFieldSpec: UIFieldSpec, total: boolean): LMDisplayFieldPSpec {
    // issue here previewing, error in formResource helper if the default label
    const labelSpec:LMLabelPSpec = {
      type: 'explicit',
      messageRule: label,
      display: label
    };

    const fieldSpec: LMFieldPSpec = {
      invalidField: false,
      fieldId: fieldId,
      inlineCompute: false,
      fieldLabel: label,
      allowUpdate: { allowUpdate: false, when: '' },
      total: total ? 'TOTAL' : 'NONE',
      canTotal: uiFieldSpec.canTotal ? true : false,
      canUpdate: uiFieldSpec.canUpdate ? true : false,
      uiFieldSpec: uiFieldSpec
    };

    return  {
      key: -1,
      type: 'field',
      label: labelSpec,
      columnWidth: 'auto',
      alerts: {},
      spec: fieldSpec
    };
  }

  public reorderDisplayField(currentIndex: number, previousIndex: number, displayField: LMDisplayFieldPSpec) {
    if (this.spec) {
      const displayFields = this.spec.displayFields ? this.spec.displayFields : [];
      if (previousIndex !== currentIndex) {
        // remove previous location
        displayFields.splice(previousIndex, 1);
        // add at new location
        displayFields.splice(currentIndex, 0, displayField);
      }
      this.builderEvent$.next(LMBuilderEvent.DISPLAY_FIELD_REORDERED);
    }
  }

  public removeDisplayField(index: number, displayField?:LMDisplayFieldPSpec) {
    if (this.spec && this.spec.displayFields) {
      this.spec.displayFields.splice(index, 1);
      if (this.spec.frozenColumn > this.spec.displayFields.length) {
        this.spec.frozenColumn = this.spec.displayFields.length;
      }
      this.builderEvent$.next(LMBuilderEvent.DISPLAY_FIELD_REMOVED);
    }
  }

  public updateDisplayFieldLabel(displayField: LMDisplayFieldPSpec, label: string) {
    const fieldSpec = displayField.type === 'field' ? displayField.spec as LMFieldPSpec: undefined;
    this.updateLabel(displayField.label, label, fieldSpec?.uiFieldSpec?.label);
  }

  private updateLabel(labelSpec: LMLabelPSpec, label: string, defaultLabel = '') {
    if (label.trim().length === 0) {
      labelSpec.type = 'noLabel';
      labelSpec.messageRule = '';
      labelSpec.display = label;
    } else if (defaultLabel === label) {
      labelSpec.type = 'default';
      labelSpec.messageRule = '';
      labelSpec.display = label;
    } else {
      labelSpec.type = 'explicit';
      labelSpec.messageRule = label;
      labelSpec.display = label;
    }
  }

  public updateFieldOperator(fieldSpec: LMFieldPSpec, fieldName: string, uiFieldSpec: UIFieldSpec, operatorInfo?: LMOperatorInfo) {
    fieldSpec.fieldId = fieldName;
    fieldSpec.uiFieldSpec = uiFieldSpec;
    fieldSpec.operatorInfo = operatorInfo;
  }

  public updateAllowUpdate(fieldSpec: LMFieldPSpec, allowUpdate: boolean, allowUpdateWhen: string) {
    fieldSpec.allowUpdate.allowUpdate = allowUpdate;
    fieldSpec.allowUpdate.when = allowUpdateWhen;
  }

  public updateDisplayFieldSort(fieldSpec: LMFieldPSpec, setName: string, isDefault: boolean, isDescending: boolean) {
    const wasDefaultSort = fieldSpec.sort ? fieldSpec.sort.default : false;
    const defaultChanged = wasDefaultSort != isDefault;

    if (isDefault && defaultChanged) {
      this.resetOtherDisplayFieldDefaultSort();
    }

    fieldSpec.sort = this._updateSortSpec(fieldSpec.sort, setName, isDefault, isDescending);
    this.builderEvent$.next(LMBuilderEvent.DISPLAY_FIELD_SORT_CHANGED);
  }

  public removeDisplayFieldSort(fieldSpec: LMFieldPSpec) {
    fieldSpec.sort = undefined;
    this.builderEvent$.next(LMBuilderEvent.DISPLAY_FIELD_SORT_CHANGED);
  }

  private resetOtherDisplayFieldDefaultSort() {
    if (this.spec) {
      if (this.spec.displayFields) {
        this.spec.displayFields.forEach(displayField => {
          if (displayField.type === 'field') {
            const fieldSpec: LMFieldPSpec = displayField.spec as LMFieldPSpec;
            if (fieldSpec.sort) {
              fieldSpec.sort.default = false;
              fieldSpec.sort.descending = false;
            }
          }
        });
      }
      if (this.spec.cardView && this.spec.cardView.sortOrders) {
        this.spec.cardView.sortOrders.forEach(cartSort => {
          cartSort.default = false;
          cartSort.descending = false;
        });
      }
    }
  }

  private _updateSortSpec(sortSpec: LMSortPSpec | undefined, setName: string, isDefault: boolean, isDescending: boolean): LMSortPSpec{
    if (sortSpec) {
      sortSpec.name = setName;
      sortSpec.default = isDefault;
      sortSpec.descending = isDefault ? isDescending : false;
      return sortSpec;
    } else {
      return  {
        key:-1,
        label: '',
        labelDisplay: '',
        type: this.spec?.implementsColumnarView ? 'field' :'set',
        name: setName,
        default: isDefault,
        descending:isDescending,
      };
    }
  }

  public buildFieldId(builderField: LMBuilderField): string {
    return this.lmBuilderService.buildFieldId(builderField);
  }

  private buildParentTypeChain(builderField: LMBuilderField): string {
      const path: Array<string> = [];
      this._buildParentTypeChain(builderField, path);
      return path.reverse().join('');
    }

  private _buildParentTypeChain(builderField: LMBuilderField, path: Array<string>) {
      const length = path.length;
      const delimeter = length > 0 ? '.' : '';
      if (builderField.facadeType === 'field' || builderField.facadeType === 'condition') {
        const facadeField = builderField.facade as LMFacadeField;
        path.push(`${facadeField.uiFieldSpec.fieldType}${delimeter}`);
      } else if (builderField.facadeType === 'relation') {
        const facadeRelation:LMFacadeRelation = builderField.facade as LMFacadeRelation;
        path.push(facadeRelation.isOTMLink ? `OTM_RELATION${delimeter}` : `OTO_RELATION${delimeter}`);
      }

      if (builderField.parent) {
        this._buildParentTypeChain(builderField.parent, path);
      }
  }

  public validate(): boolean {
    if (this.spec) {
      if (!this.validUserFolder()) {
        return false;
      }
    }

    return true;
  }

  private validUserFolder(): boolean {
    const userFolder: LMUserFolderPSpec | undefined = this.spec?.userFolder;

    if (userFolder) {
      if (!userFolder.name) {
        return false;
      }

      if (userFolder.sharing === 'actorGroup' && userFolder.sharingTarget.trim().length === 0) {
        return false;
      }

      if (userFolder.sharing === 'role' && userFolder.sharingTarget.trim().length === 0) {
        return false;
      }

      if (userFolder.sharing === 'roleGroup' && userFolder.sharingTarget.trim().length === 0) {
        return false;
      }
    }

    return true;
  }

  public save(): Observable<LMBuilderCreateReportResponse> {
    if (this.spec) {
      let url = this.router.url;
      url = this.lmUrlUtilService.addParamToUrl(url, 'requestType', 'save');
      console.log(`Save url=${url}`);
      return this.execute(url, this.spec);
    }
    this.lmLoggerService.log('no spec to save');
    return of({ status: 'VIEW_EXCEPTION', message: 'spec not found', reportUrl: '' });
  }

  public updatePreview(): Observable<LMBuilderUpdatePreviewResponse>{
    if (this.spec) {
      const modelSpec: LMListPSpec = JSON.parse(JSON.stringify(this.spec));

      if (modelSpec.userFolder && modelSpec.userFolder.search.trim().length > 0) {
          if (modelSpec.instanceSelection.where.trim().length > 0 ) {
            modelSpec.instanceSelection.where = `${modelSpec.instanceSelection.where.trim()} and (${modelSpec.userFolder.search.trim()})`;
          } else {
            modelSpec.instanceSelection.where = modelSpec.userFolder.search.trim();
          }
      }

      let url = this.router.url;
      url = this.lmUrlUtilService.addParamToUrl(url, 'requestType', 'update-preview');
      return this.execute(url, modelSpec);
    }
    this.lmLoggerService.log('no spec to update preview');
    return of({ status: 'VIEW_EXCEPTION', message: 'spec not found', lpl: '' });
  }

  private execute(url: string, payload: any): Observable<any> {
    return this.httpClient
        .post(url, payload).pipe(
            catchError(error => {
              this.lmLoggerService.error(error);
              return throwError(error || 'Server error');
            }));
  }

  private toSentenceCase(str: string): string {
    const result = str
        .replace(/([A-Z])/g, ' $1') // Add a space before every capital letter
        .trim();

    // Capitalize only the first letter of the sentence
    return result.charAt(0).toUpperCase() + result.slice(1);
  }
}

